import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { api, errMsg } from "@/lib/api";
import { Loader, Stat } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Chart = ({ title, data, k, x = "month", color = "#00B894" }) => (
  <div className="rounded-2xl border bg-card p-6"><h3 className="mb-4 text-sm font-semibold">{title}</h3>
    {data.length ? <ResponsiveContainer width="100%" height={180}><BarChart data={data}><XAxis dataKey={x} fontSize={11} /><YAxis fontSize={11} width={30} /><Tooltip /><Bar dataKey={k} fill={color} radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer> : <p className="text-sm text-muted-foreground">No data yet.</p>}
  </div>
);

export const AdminDashboard = () => {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/admin/metrics").then((r) => setM(r.data));
  }, []);
  if (!m) return <Loader />;
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div><p className="eyebrow">Business overview</p><h1 className="mt-2 text-3xl font-bold">Saflr operations</h1></div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Companies" value={m.companies} hint={`${m.active_companies} active`} testid="admin-stat-companies" />
        <Stat label="MRR" value={`£${m.mrr.toLocaleString()}`} hint={`ARR £${m.arr.toLocaleString()}`} tone="green" testid="admin-stat-mrr" />
        <Stat label="Trials / paid" value={`${m.trials} / ${m.paid}`} hint={`${m.past_due} need renewal`} tone="amber" />
        <Stat label="Users" value={m.users} hint={`${m.documents} documents`} />
        <Stat label="AI generations" value={m.generations_ok} hint={`${m.generations_failed} failed (not charged)`} />
        <Stat label="Credits consumed" value={m.credits_consumed} tone="green" />
        <Stat label="Failed payments" value={m.failed_payments} tone={m.failed_payments ? "red" : "navy"} />
        <Stat label="Open tickets" value={m.open_tickets} tone={m.open_tickets ? "amber" : "navy"} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Chart title="Revenue (paid orders, £ inc VAT)" data={m.revenue} k="gbp" />
        <Chart title="New companies" data={m.growth} k="companies" color="#0C1427" />
        <Chart title="Popular categories" data={m.popular_categories} k="value" x="name" color="#FFA000" />
      </div>
    </div>
  );
};

const Manage = ({ org, plans, close }) => {
  const [f, setF] = useState({ status: org.status, plan_slug: "", plan_status: "active", credit_adjust: "", reason: "" });
  const save = async () => {
    if (!f.reason.trim()) return toast.error("A reason is required for the audit log");
    try {
      await api.patch(`/admin/companies/${org.id}`, { ...f, plan_slug: f.plan_slug || null, credit_adjust: Number(f.credit_adjust) || null });
      toast.success("Company updated");
      close(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="company-status-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent></Select>
      <div className="grid grid-cols-2 gap-2">
        <Select value={f.plan_slug} onValueChange={(v) => setF({ ...f, plan_slug: v })}><SelectTrigger><SelectValue placeholder="Change plan…" /></SelectTrigger><SelectContent>{plans.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}</SelectContent></Select>
        <Select value={f.plan_status} onValueChange={(v) => setF({ ...f, plan_status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active (30 days)</SelectItem><SelectItem value="trialing">Trial (14 days)</SelectItem></SelectContent></Select>
      </div>
      <Input type="number" placeholder="Credit adjustment (e.g. 50 or -20)" value={f.credit_adjust} onChange={(e) => setF({ ...f, credit_adjust: e.target.value })} data-testid="credit-adjust-input" />
      <Input placeholder="Reason (required, audited)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="company-reason-input" />
      <Button className="w-full" onClick={save} data-testid="company-save">Apply changes</Button>
    </div>
  );
};

export const AdminCompanies = () => {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/companies", { params: { q: q || undefined } }).then((r) => setList(r.data));
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/admin/plans").then((r) => setPlans(r.data));
  }, []);
  return (
    <div className="space-y-6" data-testid="admin-companies">
      <div className="flex flex-wrap items-end justify-between gap-4"><h1 className="text-3xl font-bold">Companies</h1><div className="relative w-72"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies" data-testid="company-search" /></div></div>
      {!list ? <Loader /> : (
        <div className="overflow-x-auto rounded-2xl border bg-card"><table className="w-full text-sm"><thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground"><tr>{["Company", "Plan", "Status", "Credits", "Users", "Docs", "Created", ""].map((h) => <th key={h} className="p-4">{h}</th>)}</tr></thead>
          <tbody>{list.map((o) => (
            <tr key={o.id} className="border-b last:border-0" data-testid={`company-row-${o.id}`}>
              <td className="p-4 font-medium">{o.trading_name || o.registered_name}<p className="text-xs text-muted-foreground">{(o.industries || []).join(", ")}</p></td>
              <td className="p-4">{o.subscription?.plan_name}<p className="text-xs text-muted-foreground">{o.subscription?.status}</p></td>
              <td className="p-4"><span className={o.status === "suspended" ? "text-alert" : "text-health"}>{o.status}</span></td>
              <td className="p-4 font-mono text-xs">{(o.credit_monthly || 0) + (o.credit_purchased || 0)}</td><td className="p-4">{o.users}</td><td className="p-4">{o.documents}</td>
              <td className="p-4 text-xs text-muted-foreground">{o.created_at.slice(0, 10)}</td>
              <td className="p-4"><Button size="sm" variant="outline" onClick={() => setEdit(o)} data-testid={`company-manage-${o.id}`}>Manage</Button></td>
            </tr>
          ))}</tbody></table></div>
      )}
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}><DialogContent><DialogHeader><DialogTitle>Manage {edit?.trading_name || edit?.registered_name}</DialogTitle></DialogHeader>{edit && <Manage org={edit} plans={plans} close={(r) => { setEdit(null); if (r) load(); }} />}</DialogContent></Dialog>
    </div>
  );
};

const Rows = ({ rows, cols }) => (
  <div className="overflow-x-auto rounded-2xl border bg-card"><table className="w-full text-xs"><thead className="border-b text-left uppercase tracking-wider text-muted-foreground"><tr>{cols.map(([h]) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
    <tbody>{rows.length === 0 ? <tr><td className="p-6 text-muted-foreground" colSpan={cols.length}>Nothing yet.</td></tr> : rows.map((r, i) => <tr key={r.id || i} className="border-b last:border-0">{cols.map(([h, f]) => <td key={h} className="max-w-[320px] truncate p-3">{f(r)}</td>)}</tr>)}</tbody></table></div>
);

const Tickets = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/tickets").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const set = (id, status) => api.patch(`/admin/tickets/${id}`, { status }).then(load);
  return <Rows rows={rows} cols={[["Received", (r) => r.created_at.slice(0, 16)], ["From", (r) => `${r.name} <${r.email}>`], ["Subject", (r) => r.subject], ["Message", (r) => r.message], ["Status", (r) => (
    <select value={r.status} onChange={(e) => set(r.id, e.target.value)} className="rounded border bg-background px-1 py-0.5" data-testid={`ticket-status-${r.id}`}>{["open", "pending", "resolved", "closed"].map((s) => <option key={s}>{s}</option>)}</select>
  )]]} />;
};

export const AdminActivity = () => {
  const [d, setD] = useState({ audit: [], orders: [], ai: [] });
  useEffect(() => {
    Promise.all([api.get("/admin/audit"), api.get("/admin/orders"), api.get("/admin/ai-usage")]).then(([a, o, g]) => setD({ audit: a.data, orders: o.data, ai: g.data }));
  }, []);
  return (
    <div className="space-y-6" data-testid="admin-activity">
      <h1 className="text-3xl font-bold">Activity & support</h1>
      <Tabs defaultValue="tickets">
        <TabsList><TabsTrigger value="tickets" data-testid="tab-tickets">Support tickets</TabsTrigger><TabsTrigger value="orders" data-testid="tab-orders">Payments</TabsTrigger><TabsTrigger value="ai" data-testid="tab-ai-usage">AI usage</TabsTrigger><TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger></TabsList>
        <TabsContent value="tickets" className="mt-4"><Tickets /></TabsContent>
        <TabsContent value="orders" className="mt-4"><Rows rows={d.orders} cols={[["Created", (r) => r.created_at.slice(0, 16)], ["Description", (r) => r.description], ["Amount", (r) => `£${(r.amount_minor / 100).toFixed(2)}`], ["Status", (r) => r.status], ["Revolut order", (r) => r.revolut_order_id || "—"]]} /></TabsContent>
        <TabsContent value="ai" className="mt-4"><Rows rows={d.ai} cols={[["When", (r) => r.created_at.slice(0, 16)], ["Kind", (r) => r.kind], ["Category", (r) => r.category || "—"], ["Status", (r) => r.status], ["Credits", (r) => r.credits], ["Error", (r) => r.error || ""]]} /></TabsContent>
        <TabsContent value="audit" className="mt-4"><Rows rows={d.audit} cols={[["When", (r) => r.created_at.slice(0, 19)], ["Action", (r) => r.action], ["Actor", (r) => r.actor_email || "system"], ["Org", (r) => r.org_id?.slice(0, 8) || "—"], ["Target", (r) => r.target || ""]]} /></TabsContent>
      </Tabs>
    </div>
  );
};
